import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "engagement-report-chart__title" };
import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import i18n from '@/i18n';
import moment from 'moment';
// utils
import { formatPercent, formatDecimal } from '@/utilities/formatters';
// components
import BaseLoader from '@/components/_atoms/BaseLoaderSpinner.vue';
import InfoCard from '@/components/_atoms/InfoCard.vue';
export default /*#__PURE__*/ _defineComponent({
    __name: 'Graphic',
    setup(__props) {
        // libs
        const store = useStore();
        // computed
        const graphic = computed(() => store.getters['engagementReport/getGraphic']);
        const isLoading = computed(() => store.getters['engagementReport/getIsIndicatorsLoading']);
        const hasError = computed(() => store.getters['engagementReport/getHasIndicatorsError']);
        const hasData = computed(() => store.getters['engagementReport/setHasDataOnGraphic']);
        const seriesGraphic = computed(() => [
            {
                name: i18n.global.t('campaign.engagement-report.graphic.suggestions-available'),
                type: 'column',
                color: '#D9E0F4',
                data: graphic?.value.metrics?.suggestionsAvailable,
                marker: { symbol: 'circle' },
            },
            {
                name: i18n.global.t('campaign.engagement-report.graphic.suggestions-used'),
                type: 'column',
                color: '#6C90F4',
                data: graphic?.value.metrics?.suggestionsUsed,
                marker: { symbol: 'circle' },
            },
            {
                name: i18n.global.t('campaign.engagement-report.graphic.percentage-suggestions-used'),
                type: 'line',
                color: '#FF3162',
                data: graphic?.value.metrics?.percentageSuggestionsUsed,
                marker: { symbol: 'line' },
                yAxis: 1,
            },
        ]);
        // actions
        const fetchIndicators = () => store.dispatch('engagementReport/setFetchIndicators');
        // computed
        const getDatepickerDates = computed(() => store.getters['dates']);
        const maxLeftYAxis = computed(() => {
            return Math.max(...seriesGraphic?.value[0]?.data);
        });
        const getChartOptions = computed(() => ({
            chart: {
                zoomType: 'xy',
                height: 400,
                events: {
                    load: function () {
                        document.querySelectorAll('.highcharts-legend-item path').forEach(function (element) {
                            element.setAttribute('stroke-width', '5');
                        });
                    },
                },
                style: {
                    fontFamily: 'Montserrat',
                    margin: '0 auto',
                },
            },
            title: {
                text: null,
            },
            legend: {
                itemMarginTop: -30,
                itemMarginBottom: 35,
                itemDistance: 40,
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                itemStyle: {
                    color: '#555',
                    fontWeight: 600,
                },
            },
            tooltip: {
                shared: true,
                useHTML: true,
                borderWidth: 0,
                backgroundColor: '#fff',
                padding: 14,
                formatter: function formater() {
                    const suggestions_used = i18n.global.t('campaign.engagement-report.graphic.suggestions-used');
                    const suggestions_available = i18n.global.t('campaign.engagement-report.graphic.suggestions-available');
                    const htmlTooltip = this.points.map(info => {
                        if (info.series.name === suggestions_available) {
                            return `<div><b>${info.key}</b><div style="display: flex; align-items: center; margin-bottom: 5px; margin-top: 8px"><div style="color:${info.color}; margin-right: 5px; height: 10px; width: 10px ;background-color:${info.color}; border-radius: 50%"></div>${info.series.name}: <b style="margin-left: 3px">${formatDecimal(info.y)}</b></div> </div>`;
                        }
                        else if (info.series.name === suggestions_used) {
                            return `<div><div style="display: flex; align-items: center; margin-bottom: 5px; margin-top: 8px"><div style="color:${info.color}; margin-right: 5px; height: 10px; width: 10px ;background-color:${info.color}; border-radius: 50%"></div>${info.series.name}: <b style="margin-left: 3px">${formatDecimal(info.y)}</b></div> </div>`;
                        }
                        else {
                            return `<div style="display: flex; align-items: center; margin-bottom: 5px"><div style="color:${info.color}; margin-right: 5px; height: 5px; width: 10px ;background-color:${info.color} "></div><span>${info.series.name}</span>: <b style="margin-left: 3px">${info.y.toString().replace('.', ',')}%</b></div> `;
                        }
                    });
                    return htmlTooltip;
                },
            },
            plotOptions: {
                series: {
                    marker: { enabled: false },
                },
                column: {
                    grouping: false,
                    shadow: false,
                    borderWidth: 0,
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
            },
            credits: {
                enabled: false,
            },
            xAxis: {
                labels: {
                    rotation: -45,
                },
                categories: graphic?.value?.labels?.map((date) => {
                    if (date.split('-').length === 3) {
                        return moment(date).format('DD/MM/YYYY');
                    }
                    else {
                        return moment(date).format('MM/YYYY');
                    }
                }),
                tickWidth: 0,
            },
            yAxis: [
                {
                    title: {
                        text: null,
                    },
                    max: maxLeftYAxis.value,
                    tickPositions: [
                        0,
                        maxLeftYAxis.value * 0.2,
                        maxLeftYAxis.value * 0.4,
                        maxLeftYAxis.value * 0.6,
                        maxLeftYAxis.value * 0.8,
                        maxLeftYAxis.value,
                    ],
                    lineWidth: 0,
                    labels: {
                        style: {
                            width: '60px',
                            whiteSpace: 'normal',
                        },
                        formatter: function formatter(data) {
                            return formatDecimal(data.value);
                        },
                    },
                },
                {
                    tickPositions: [0, 20, 40, 60, 80, 100],
                    max: 100,
                    lineWidth: 0,
                    opposite: true,
                    title: {
                        text: null,
                    },
                    labels: {
                        style: {
                            width: '60px',
                            whiteSpace: 'normal',
                        },
                        formatter: function formatter(data) {
                            return formatPercent(data.value / 100);
                        },
                    },
                },
            ],
            series: seriesGraphic.value,
        }));
        watch(() => getDatepickerDates, () => {
            fetchIndicators();
        }, { deep: true });
        onMounted(() => {
            fetchIndicators();
        });
        return (_ctx, _cache) => {
            const _component_highcharts = _resolveComponent("highcharts");
            const _component_base_card_v2 = _resolveComponent("base-card-v2");
            return (_openBlock(), _createBlock(_component_base_card_v2, { class: "engagement-report-chart" }, {
                default: _withCtx(() => [
                    (!isLoading.value && hasData.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createElementVNode("strong", _hoisted_1, _toDisplayString(_ctx.$t('campaign.engagement-report.graphic.title')), 1),
                            _createVNode(_component_highcharts, {
                                options: getChartOptions.value,
                                class: "engagement-report-chart__highcharts"
                            }, null, 8, ["options"])
                        ], 64))
                        : _createCommentVNode("", true),
                    (isLoading.value)
                        ? (_openBlock(), _createBlock(BaseLoader, { key: 1 }))
                        : _createCommentVNode("", true),
                    (!hasData.value && !isLoading.value)
                        ? (_openBlock(), _createBlock(InfoCard, {
                            key: 2,
                            ref: "message-error",
                            error: hasError.value
                        }, null, 8, ["error"]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
            }));
        };
    }
});
